import ObjectHeading from "@/components/ObjectHeading";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TableHeading, Row } from "@/components/Table";
import Table from "@/components/Table";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import FileUpload from "@/components/FileUpload";
import Progress from "@/components/Progress";
import { useMutation } from "@apollo/client";
import { UPLOAD_ROSTER_PREVIEW_MUTATION, SUBMIT_STUDENT_ROSTER_MUTATION } from "../../../mutations";
import {
  UploadRosterPreviewMutationResponse,
  UploadRosterMutationVariables,
  SubmitStudentRosterMutationResponse,
  SubmitStudentRosterMutationVariables,
  BackofficeCRMValidatedStudentRecordInput
} from "../../../types";
import { useQuery } from "@apollo/client";
import { UNIVERSITY_QUERY } from "../../queries";
import Tooltip from "@/components/Tooltip";
import Banner from "@/components/Banner";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  buildRosterUploadFeedback,
  extractTopLevelErrorMessages,
  getRosterValidationGuidanceBanner
} from "./uploadRosterFeedback";

/** Keep preview warning `details` (including accepted hours) for submitStudentRoster. Backend: pgahq/pga-backend#10604. */
function mapPreviewIssues(issues: unknown) {
  if (!Array.isArray(issues)) return [];

  return issues.map((issue: { field?: string; message?: string; details?: unknown }) => ({
    field: issue.field || "",
    message: issue.message || "",
    details: issue.details && typeof issue.details === "object" ? issue.details : {}
  }));
}

export default function UploadRosterPage() {
  const { universityId } = useParams();
  const navigate = useNavigate();
  const { data } = useQuery(UNIVERSITY_QUERY, { variables: { legacyId: universityId } });
  const university = data?.universities?.[0];
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isFinalizeDialogOpen, setIsFinalizeDialogOpen] = useState(false);
  const [selectedProgramVersion, setSelectedProgramVersion] = useState<string>("PGM 3.1");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorBeforeDialog, setErrorBeforeDialog] = useState<string | null>(null);
  const skipErrorRestoreOnCloseRef = useRef(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [hasPreviewErrors, setHasPreviewErrors] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [previewRoster, { loading }] = useMutation<UploadRosterPreviewMutationResponse, UploadRosterMutationVariables>(
    UPLOAD_ROSTER_PREVIEW_MUTATION
  );
  const [submitStudentRoster, { loading: submitLoading }] = useMutation<
    SubmitStudentRosterMutationResponse,
    SubmitStudentRosterMutationVariables
  >(SUBMIT_STUDENT_ROSTER_MUTATION);

  if (!universityId) {
    return <div>University not found.</div>;
  }

  const backLink = {
    to: "/education/universities/:universityId/students" as const,
    params: { universityId },
    title: "Back"
  };

  const programVersions = ["PGM 3.0", "PGM 3.1"];

  const handleUpload = async (file: File) => {
    setSelectedFile(file);
    setError(null);
    setFileUploadError(null);
  };

  const handleFileError = (errorMessage: string) => {
    setFileUploadError(errorMessage);
  };

  // Helper to map program version to backend code (S0 or V0)
  function mapProgramVersionToBackendCode(version: string): string {
    const versionMap: Record<string, string> = {
      "PGM 3.0": "S0",
      "PGM 3.1": "V0"
    };
    return versionMap[version] || "";
  }

  // Helper to map backend code to display label (e.g. V0 -> PGM 3.1 Level 0)
  function mapBackendCodeToDisplayLabel(code: string): string {
    if (!code || code.length < 2) return code;
    const versionMap: Record<string, string> = {
      S: "PGM 3.0",
      V: "PGM 3.1"
    };
    const prefix = code[0];
    const level = code[1];
    const version = versionMap[prefix] || code;
    return `${version} Level ${level}`;
  }

  const handleUploadRosterClick = async () => {
    if (!selectedFile) {
      setFileUploadError("Please select a file to upload.");
      return;
    }

    setError(null);
    setFileUploadError(null);

    try {
      const programLevelCode = mapProgramVersionToBackendCode(selectedProgramVersion);
      const result = await previewRoster({
        variables: {
          file: selectedFile,
          programLevel: programLevelCode,
          universitySectionId: universityId
        }
      });

      // Always show preview data regardless of success/error status
      // This allows us to display validation results in the preview screen
      const preview = result.data?.universityImportRosterPreview;
      const validRecords = preview?.previewValidRecords?.map((record) => record.studentData) || [];
      const errorRecords = preview?.previewErrors || [];
      const warningRecords = preview?.previewWarnings || [];
      const topLevelErrors = extractTopLevelErrorMessages(preview?.errors);
      const hasPreviewRows = validRecords.length > 0 || errorRecords.length > 0 || warningRecords.length > 0;
      const hasErrors = topLevelErrors.length > 0 || errorRecords.length > 0;

      if (!hasPreviewRows) {
        // Format/parse failures and empty files: keep the dialog open with a clear message.
        // Preserve any existing preview so a failed re-upload does not wipe prior validation results.
        const feedback = buildRosterUploadFeedback({
          topLevelErrors,
          validCount: validRecords.length,
          errorCount: errorRecords.length,
          warningCount: warningRecords.length
        });
        setError(
          feedback ||
            "We couldn't read this roster file. Download the template and confirm the column headers match before uploading again."
        );
        return;
      }

      // Set error state for button logic only after a response yields preview rows
      setHasPreviewErrors(hasErrors);

      // Combine all records for preview display
      const allPreviewData = [
        ...validRecords,
        ...errorRecords.map((record) => ({
          ...record.studentData,
          _hasErrors: true,
          _errors: mapPreviewIssues(record.issues)
        })),
        ...warningRecords.map((record) => ({
          ...record.studentData,
          _hasWarnings: true,
          _warnings: mapPreviewIssues(record.issues)
        }))
      ];

      // Sort by status: Row Errors first, then Errors, then Warnings, then Valid
      const sortedPreviewData = allPreviewData.sort((a, b) => {
        return getRowStatusRank(a) - getRowStatusRank(b);
      });

      setPreviewData(sortedPreviewData);
      skipErrorRestoreOnCloseRef.current = true;
      setIsDialogOpen(false);
      // All-rows-failed copy is folded into the error guidance banner (not stored as a
      // page error that would be hidden when that banner's variant is "error").
      setError(null);
    } catch (e: any) {
      setError(
        e.message ||
          "We couldn't read this roster file. Check that it is a valid CSV matching the template and try again."
      );
    }
  };

  const generateCSVTemplate = () => {
    // Headers must match Import::UniversityRoster::TEMPLATE_HEADERS in pga-backend
    // (REQUIRED_HEADERS plus optional Preferred First Name after First name)
    const universityHeader = ",,,,,,,,Sample PGA University,,,,,,,,,\n";
    const studentReportHeader = ",,,,,,,,Fall 2025 Entering Student Report,,,,,,,,,\n";
    const studentInfoColumns =
      "First name,Preferred First Name,Last name,PGA ID,Gender,Street Address (Home not Campus),City,State,Zip Code,Country Code,Primary Phone,University Email,Date of Birth,Entering (Handicap),Start Date of School,Transfer Y/N,Transfer Hours Total,Transfer Hours Accepted\n";
    const exampleRow =
      "John,Johnny,Doe,12345678,M,123 Main St,Frisco,TX,75033,USA,4695551234,john.doe@university.edu,2006-06-23,11.0,2025-08-19,N,,\n";
    const transferExampleRow =
      "Jane,,Smith,,F,456 Oak Ave,Frisco,TX,75034,USA,4695555678,jane.smith@university.edu,2005-03-15,8.5,2025-08-19,Y,45,30\n";

    return universityHeader + studentReportHeader + studentInfoColumns + exampleRow + transferExampleRow;
  };

  const downloadCSVTemplate = () => {
    const csvContent = generateCSVTemplate();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "student-roster-template.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => {
    showSnackbar("Template download started");
    downloadCSVTemplate();
    setIsDownloadDialogOpen(false);
  };

  const handleFinalizeRosterClick = () => {
    setIsFinalizeDialogOpen(true);
  };

  const handleFinalizeRosterConfirm = async () => {
    try {
      // Prepare the data for submission
      const validRecords: BackofficeCRMValidatedStudentRecordInput[] = previewData
        .filter((record) => !record._hasErrors && !record._hasWarnings)
        .map((record, index) => ({
          row: index + 1,
          studentData: record,
          issues: []
        }));

      const warningRecords: BackofficeCRMValidatedStudentRecordInput[] = previewData
        .filter((record) => record._hasWarnings && !record._hasErrors)
        .map((record, index) => ({
          row: index + 1,
          studentData: record,
          issues: record._warnings || []
        }));

      const result = await submitStudentRoster({
        variables: {
          universityId,
          validRecords,
          warningRecords: warningRecords.length > 0 ? warningRecords : undefined
        }
      });

      if (result.data?.submitStudentRoster.success) {
        showSnackbar("Roster submitted successfully!");
        setIsFinalizeDialogOpen(false);
        // Navigate to the Current Students page
        navigate(`/education/universities/${universityId}/students`);
      } else {
        const errors = result.data?.submitStudentRoster.errors || [];
        const errorMessage =
          errors.length > 0
            ? errors
                .map((e) => e.base)
                .filter(Boolean)
                .join(", ")
            : "Failed to submit roster";
        setError(errorMessage);
      }
    } catch (e: any) {
      setError(e.message || "An unexpected error occurred while submitting the roster");
    }
  };

  const handleFinalizeDialogCancel = () => {
    setIsFinalizeDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setIsDialogOpen(false);
    setSelectedFile(null);
    setFileUploadError(null);
    setSelectedProgramVersion("PGM 3.1");
    setFileUploadKey((prev) => prev + 1);

    if (skipErrorRestoreOnCloseRef.current) {
      skipErrorRestoreOnCloseRef.current = false;
      return;
    }

    // Restore the page error from when the dialog opened so a failed re-parse
    // does not leave a format-error banner over a preserved preview table.
    setError(errorBeforeDialog);
    if (previewData.length === 0) {
      setHasPreviewErrors(false);
    }
  };

  const previewColumns = [
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: { row: Row<any> }) => {
        const hasErrors = row.original._hasErrors;
        const hasWarnings = row.original._hasWarnings;
        const rowLevelErrors = getRowLevelErrors(row.original);

        if (rowLevelErrors.length > 0) {
          // Map all field names in row error messages to CSV headers
          const formattedRowErrors = rowLevelErrors.map((e: any) => formatErrorMessage(e.message));
          return (
            <Tooltip
              title={
                <div>
                  {formattedRowErrors.map((msg: string, idx: number) => (
                    <div key={idx}>{msg}</div>
                  ))}
                </div>
              }
              color="black"
              variant="plain"
              supportingText=""
              positioning="fixed"
            >
              <span className="flex items-center gap-1 cursor-help">
                <Icon name="error" size={18} className="text-red-700 relative -bottom-0.5 mr-1" />
                <span className="text-red-700 text-sm font-medium">Row Error</span>
              </span>
            </Tooltip>
          );
        }
        if (hasErrors) {
          return (
            <div className="flex items-center gap-2">
              <Icon name="error" size={16} className="text-red-600" />
              <span className="text-red-600 text-sm font-medium">Error</span>
            </div>
          );
        } else if (hasWarnings) {
          // Check if there are row-level warnings to show in tooltip
          const rowLevelWarnings = getRowLevelWarnings(row.original);
          if (rowLevelWarnings.length > 0) {
            const formattedRowWarnings = rowLevelWarnings.map((w: any) => formatErrorMessage(w.message));
            return (
              <Tooltip
                title={
                  <div>
                    {formattedRowWarnings.map((msg: string, idx: number) => (
                      <div key={idx}>{msg}</div>
                    ))}
                  </div>
                }
                color="black"
                variant="plain"
                supportingText=""
                positioning="fixed"
              >
                <span className="flex items-center gap-1 cursor-help">
                  <Icon name="warning" size={16} className="text-amber-600" />
                  <span className="text-amber-600 text-sm font-medium">Warning</span>
                </span>
              </Tooltip>
            );
          } else {
            // Show warning icon and label only, no tooltip/hover
            return (
              <span className="flex items-center gap-1">
                <Icon name="warning" size={16} className="text-amber-600" />
                <span className="text-amber-600 text-sm font-medium">Warning</span>
              </span>
            );
          }
        } else {
          return (
            <div className="flex items-center gap-2">
              <Icon name="check_circle" size={16} className="text-green-600" />
              <span className="text-green-600 text-sm font-medium">Valid</span>
            </div>
          );
        }
      }
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 180,
      minSize: 140,
      cell: ({ row }: { row: Row<any> }) => (
        <div className="flex flex-col gap-0.5 min-w-0 max-w-[180px]">
          <span className="text-body-medium-semibold text-sys-brand-on-surface break-words">
            {renderCellWithError(row.original.first_name, row.original, "first_name")}{" "}
            {renderCellWithError(row.original.last_name, row.original, "last_name")}
          </span>
          <span className="text-body-small text-sys-brand-on-surface-variant">
            PGA ID: {renderCellWithError(row.original.pga_id, row.original, "pga_id")}
          </span>
        </div>
      )
    },
    {
      accessorKey: "gender",
      header: "Gender",
      cell: ({ row }: { row: Row<any> }) => renderCellWithError(row.original.gender, row.original, "gender")
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }: { row: Row<any> }) => renderCellWithError(row.original.email, row.original, "email")
    },
    {
      accessorKey: "country_code",
      header: "Country",
      cell: ({ row }: { row: Row<any> }) => renderCellWithError(row.original.country_code, row.original, "country_code")
    },
    {
      accessorKey: "mobile_phone",
      header: "Mobile Phone",
      cell: ({ row }: { row: Row<any> }) =>
        renderCellWithError(
          formatPhoneNumberDisplay(row.original.phone_number, row.original.country_code),
          row.original,
          "phone_number"
        )
    },
    {
      accessorKey: "date_of_birth",
      header: "Date of Birth",
      cell: ({ row }: { row: Row<any> }) =>
        renderCellWithError(row.original.date_of_birth, row.original, "date_of_birth")
    },
    {
      accessorKey: "program_level",
      header: "Program / Level",
      cell: ({ row }: { row: Row<any> }) => (
        <span className="text-body-small text-sys-brand-on-surface-variant pt-0.5">
          {mapBackendCodeToDisplayLabel(row.original.program_level)}
        </span>
      )
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      cell: ({ row }: { row: Row<any> }) =>
        renderCellWithError(row.original.start_date_of_school, row.original, "start_date_of_school")
    },
    {
      accessorKey: "entering_handicap",
      header: "Handicap",
      cell: ({ row }: { row: Row<any> }) =>
        renderCellWithError(row.original.entering_handicap, row.original, "entering_handicap")
    },
    {
      accessorKey: "is_transfer_student",
      header: "Transfer Student",
      cell: ({ row }: { row: Row<any> }) =>
        renderCellWithError(row.original.is_transfer_student, row.original, "is_transfer_student")
    },
    {
      accessorKey: "transfer_hours_total",
      header: "Transfer Hours Total",
      cell: ({ row }: { row: Row<any> }) =>
        renderCellWithError(row.original.transfer_hours_total, row.original, "transfer_hours_total")
    },
    {
      accessorKey: "transfer_hours_accepted",
      header: "Transfer Hours Accepted",
      cell: ({ row }: { row: Row<any> }) =>
        renderCellWithError(row.original.transfer_hours_accepted, row.original, "transfer_hours_accepted")
    }
  ];

  // Helper function to get error or warning message for a specific field (exact match only)
  const getFieldError = (row: any, fieldName: string, type: "error" | "warning" = "error") => {
    const arr = type === "error" ? row._errors : row._warnings;
    if (!arr) return null;

    // Try exact match first
    let fieldError = arr.find((issue: any) => issue.field === fieldName);

    // If no exact match, try common field name variations for handicap
    if (!fieldError && fieldName === "entering_handicap") {
      fieldError = arr.find(
        (issue: any) =>
          issue.field === "handicap" || issue.field === "entering_handicap" || issue.field === "entering_handicap_index"
      );
    }

    return fieldError?.message || null;
  };

  // Map backend field names to CSV header names
  const csvHeaderMap: Record<string, string> = {
    first_name: "Student First Name",
    informal_name: "Preferred First Name",
    last_name: "Student Last Name",
    pga_id: "PGA ID",
    gender: "Gender",
    address1: "Street Address (Home not Campus)",
    address_city: "City",
    address_state: "State",
    address_zip: "Zip Code",
    address_country_code: "Country Code",
    country_code: "Country Code",
    phone_number: "Primary Phone",
    email: "University Email",
    date_of_birth: "Date of Birth",
    program_level: "Entering",
    start_date_of_school: "Start Date of School",
    entering_handicap: "Handicap"
  };

  // Utility function to format error messages using CSV header names
  const formatErrorMessage = (errorMessage: string, fieldName?: string) => {
    // Helper to humanize a backend key (e.g., first_name -> First name)
    const humanize = (str: string) =>
      str
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .replace(/\bOf\b/, "of");

    for (const [key, csvHeader] of Object.entries(csvHeaderMap)) {
      const humanized = humanize(key);
      const regexKey = new RegExp(key, "i");
      const regexHumanized = new RegExp(humanized, "i");
      if (regexKey.test(errorMessage)) {
        return errorMessage.replace(regexKey, `"${csvHeader}"`);
      }
      if (regexHumanized.test(errorMessage)) {
        return errorMessage.replace(regexHumanized, `"${csvHeader}"`);
      }
    }
    // If a fieldName is provided, prefix the error with the quoted header
    if (fieldName && csvHeaderMap[fieldName]) {
      return `"${csvHeaderMap[fieldName]}" ${errorMessage}`;
    }
    return errorMessage;
  };

  // Helper function to get row-level errors (errors not tied to any displayed field)
  const displayedFields = [
    "first_name",
    "last_name",
    "pga_id",
    "gender",
    "email",
    "phone_number",
    "date_of_birth",
    "program_level",
    "start_date_of_school",
    "entering_handicap",
    "transfer_hours_total",
    "transfer_hours_accepted",
    "country_code"
  ];
  const getRowLevelErrors = (row: any) => {
    if (!row._errors) return [];
    return row._errors.filter((error: any) => !displayedFields.includes(error.field));
  };

  const getRowLevelWarnings = (row: any) => {
    if (!row._warnings) return [];
    return row._warnings.filter((warning: any) => {
      return !displayedFields.includes(warning.field);
    });
  };

  // Helper to rank row status for sorting: Row Errors (0), Errors (1), Warnings (2), Valid (3)
  function getRowStatusRank(row: any) {
    const hasRowErrors = getRowLevelErrors(row).length > 0;
    if (hasRowErrors) return 0;
    if (row._hasErrors) return 1;
    if (row._hasWarnings) return 2;
    return 3;
  }

  // Helper function to render cell with error/warning icon and tooltip (only for field-level errors/warnings)
  const renderCellWithError = (value: any, row: any, fieldName: string) => {
    const errorMessage = getFieldError(row, fieldName, "error");
    const warningMessage = getFieldError(row, fieldName, "warning");
    const isError = row._hasErrors && errorMessage;
    const isWarning = row._hasWarnings && warningMessage;

    // Format the error/warning message to use the CSV header
    const formattedError = errorMessage ? formatErrorMessage(errorMessage, fieldName) : "";
    const formattedWarning = warningMessage ? formatErrorMessage(warningMessage, fieldName) : "";

    if (isError) {
      return (
        <div className="flex items-center gap-1">
          <Tooltip title={formattedError} color="black" variant="plain" supportingText="">
            <span className="flex items-center cursor-help">
              <Icon name="error" size={18} className="text-red-700 relative -bottom-0.5 mr-1" />
            </span>
          </Tooltip>
          <span className="text-body-small text-sys-brand-on-surface-variant pt-0.5">{value || "--"}</span>
        </div>
      );
    }
    if (isWarning) {
      return (
        <div className="flex items-center gap-1">
          <Tooltip title={formattedWarning} color="black" variant="plain" supportingText="">
            <span className="flex items-center cursor-help">
              <Icon name="warning" size={18} className="text-amber-500 relative -bottom-0.5 mr-1" />
            </span>
          </Tooltip>
          <span className="text-body-small text-sys-brand-on-surface-variant pt-0.5">{value || "--"}</span>
        </div>
      );
    }
    return <span className="text-body-small text-sys-brand-on-surface-variant pt-0.5">{value || "--"}</span>;
  };

  // Normalize country code logic
  const normalizeCountryCode = (countryCode: string | undefined | null) => {
    if (!countryCode) return null;
    const code = countryCode.toUpperCase().trim();
    switch (code) {
      case "USA":
      case "UNITED STATES":
      case "US":
        return "US";
      case "CAN":
      case "CANADA":
      case "CA":
        return "CA";
      default:
        return code;
    }
  };

  // US/CA phone number formatting utility
  const formatUSPhoneNumber = (phone: string | undefined | null) => {
    if (!phone) return "--";
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) return phone;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Country code to numeric mapping
  const countryCodeToNumeric: Record<string, string> = {
    US: "1",
    CA: "1",
    GB: "44",
    FR: "33",
    DE: "49",
    AU: "61",
    IN: "91"
  };

  // International phone number formatting
  const formatPhoneNumberDisplay = (phone: string | undefined | null, countryCode: string | undefined | null) => {
    // Default to US if country code is missing or blank
    const normCode = normalizeCountryCode(countryCode && countryCode.length > 0 ? countryCode : "US");
    const numericCode = normCode ? countryCodeToNumeric[normCode] || normCode : undefined;
    if (normCode === "US" || normCode === "CA" || !normCode) {
      return formatUSPhoneNumber(phone);
    }
    if (!phone) return "--";
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      return numericCode
        ? `+${numericCode} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
        : formatUSPhoneNumber(phone);
    }
    return numericCode ? `+${numericCode} ${phone}` : phone;
  };

  const openUploadDialog = () => {
    setErrorBeforeDialog(error);
    setIsDialogOpen(true);
  };

  const hasPreview = previewData.length > 0;
  const isReadyToFinalize = hasPreview && !hasPreviewErrors;
  const warningCount = previewData.filter((record) => record._hasWarnings && !record._hasErrors).length;
  const previewErrorCount = previewData.filter((record) => record._hasErrors).length;
  const previewValidCount = previewData.filter((record) => !record._hasErrors && !record._hasWarnings).length;
  const validationGuidanceBanner = hasPreview
    ? getRosterValidationGuidanceBanner({
        hasErrors: hasPreviewErrors,
        hasWarnings: warningCount > 0,
        validCount: previewValidCount,
        errorCount: previewErrorCount
      })
    : null;
  const showOperationalErrorBanner = Boolean(error) && validationGuidanceBanner?.variant !== "error";
  const showPageBanners = !isDialogOpen && !isFinalizeDialogOpen;

  const previewHeadingActions = isReadyToFinalize
    ? []
    : [
        {
          label: "Upload Roster",
          icon: "upload" as const,
          variant: "filled" as const,
          onClick: openUploadDialog,
          testId: "upload-roster-button"
        }
      ];

  return (
    <FocusedPageLayout
      backLink={backLink}
      stickyActions={hasPreview}
      actions={
        hasPreview ? (
          <Button
            icon={submitLoading ? undefined : "check_circle"}
            variant="filled"
            onClick={handleFinalizeRosterClick}
            disabled={submitLoading || hasPreviewErrors}
            testId="finalize-roster-button"
          >
            {submitLoading ? (
              <>
                <Progress size={20} className="mr-2" />
                Finalizing...
              </>
            ) : (
              "Finalize Roster"
            )}
          </Button>
        ) : undefined
      }
      renderBanner={
        showPageBanners && (validationGuidanceBanner || showOperationalErrorBanner)
          ? () => (
              <div className="mx-6 md:mx-12 mb-4">
                <div className="max-w-[1440px] mx-auto flex flex-col gap-3">
                  {validationGuidanceBanner && (
                    <Banner
                      variant={validationGuidanceBanner.variant}
                      description={validationGuidanceBanner.description}
                      inline={false}
                      testId={validationGuidanceBanner.testId}
                    />
                  )}
                  {showOperationalErrorBanner && error && (
                    <Banner
                      variant="error"
                      description={error}
                      inline={false}
                      closable={true}
                      onClose={() => setError(null)}
                      testId="roster-upload-error-banner"
                    />
                  )}
                </div>
              </div>
            )
          : undefined
      }
    >
      <ObjectHeading
        title={university?.name || "Upload Roster"}
        metaData={
          hasPreview
            ? [
                {
                  text: String(previewData.length),
                  label: isReadyToFinalize ? "Students to finalize" : "Records for validation"
                }
              ]
            : undefined
        }
        iconName="account_balance"
        imageUrl={university?.logoUrl || undefined}
        isDetailsView
        showFullMetadataInCondensed
      />

      {hasPreview ? (
        <>
          <TableHeading
            heading={isReadyToFinalize ? "Ready to Finalize Roster" : "Roster Validation Preview"}
            body={
              isReadyToFinalize
                ? warningCount > 0
                  ? `Your roster is ready. ${warningCount} record(s) have warnings and will still be imported. Review the preview below, then finalize to mark these students eligible for enrollment.`
                  : "Your roster is ready. Review the preview below, then finalize to mark these students eligible for enrollment."
                : previewData.some((record) => !record._hasErrors)
                  ? "Some records have errors and will not be imported. Upload a corrected roster, or review the issues below."
                  : "None of the rows in this file passed validation. Upload a corrected roster, or review the issues below."
            }
            actions={previewHeadingActions}
          />
          <div data-testid="roster-preview-table">
            <Table loading={loading} columns={previewColumns} data={previewData} />
          </div>
        </>
      ) : (
        <div
          className="mx-auto mt-8 max-w-xl rounded-lg border border-sys-brand-outline px-8 py-10 text-center"
          data-testid="roster-upload-empty-state"
        >
          <div className="mx-auto flex h-[70px] w-[70px] items-center justify-center rounded-full bg-sys-brand-tertiary">
            <Icon name="upload_file" size={40} className="text-sys-brand-on-tertiary" />
          </div>
          <h2 className="mt-6 mb-0 text-title-large-bold font-serif text-sys-brand-on-surface">
            New PGM Program Students
          </h2>
          <p className="mt-2 mb-0 text-body-medium text-sys-brand-on-surface-variant">
            Add new students to the program by uploading a new enrollment roster. Students who are not in Member 360 yet
            will have new profiles created once the roster is finalized.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button
              variant="text"
              icon="download"
              onClick={() => setIsDownloadDialogOpen(true)}
              testId="download-template-button"
            >
              Download Template
            </Button>
            <Button variant="filled" icon="upload" onClick={openUploadDialog} testId="upload-roster-button">
              Upload Roster
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={isDownloadDialogOpen}
        onClosed={() => setIsDownloadDialogOpen(false)}
        headline="Download Roster Template"
        className="w-full max-w-[490px]"
        actions={
          <>
            <Button variant="text" onClick={() => setIsDownloadDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="filled" icon="download" onClick={handleDownloadTemplate}>
              Download Template
            </Button>
          </>
        }
      >
        <p data-testid="download-template-instructions" className="text-body-medium text-sys-brand-on-surface">
          <span className="font-bold">Instructions:</span> Download the CSV template and fill out a row for each
          incoming student. Use a country code like <span className="font-bold">USA</span> (not a phone dialing code
          such as 1), and a numeric start date such as <span className="font-bold">8/17/26</span> or{" "}
          <span className="font-bold">2025-08-19</span>. Follow the sample rows, then save and upload the file.
          Validation issues are caught during upload.
        </p>
      </Dialog>

      <Dialog
        headline="Upload Student Roster"
        displayOverflow
        hasFormElements
        open={isDialogOpen}
        onClosed={handleDialogCancel}
        className="w-full max-w-[490px]"
        actions={
          <>
            <Button variant="text" theme="light" onClick={handleDialogCancel}>
              Cancel
            </Button>
            <Button
              variant="filled"
              icon={loading ? undefined : "upload"}
              onClick={handleUploadRosterClick}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Progress size={20} className="mr-2" />
                  Uploading...
                </>
              ) : (
                "Upload Roster"
              )}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-6 overflow-visible">
          {error && (
            <Banner variant="error" description={error} inline={true} closable={true} onClose={() => setError(null)} />
          )}
          <Select
            className="w-full"
            optionListMaxHeight="extended"
            required
            label="Program Version"
            value={selectedProgramVersion}
            onSelect={(value: string) => setSelectedProgramVersion(value)}
            options={programVersions.map((version) => ({ value: version, label: version }))}
            placeholder="Select a program version"
          />
          <FileUpload
            key={fileUploadKey}
            overline="Student Roster"
            label="Choose a file to upload"
            subtitle="Allowed file extensions: csv"
            buttonText="Upload"
            icon="upload"
            handleUpload={handleUpload}
            accept=".csv"
            loading={loading}
            errorMsg={fileUploadError || undefined}
            onError={handleFileError}
          />
        </div>
      </Dialog>

      <Dialog
        open={isFinalizeDialogOpen}
        onClosed={() => setIsFinalizeDialogOpen(false)}
        headline="Finalize Roster"
        className="w-full max-w-[490px]"
        actions={
          <>
            <Button variant="text" theme="light" onClick={handleFinalizeDialogCancel}>
              Cancel
            </Button>
            <Button
              variant="filled"
              icon={submitLoading ? undefined : "check_circle"}
              onClick={handleFinalizeRosterConfirm}
              disabled={submitLoading}
            >
              {submitLoading ? (
                <>
                  <Progress size={20} className="mr-2" />
                  Submitting...
                </>
              ) : (
                "Finalize Roster"
              )}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && (
            <Banner variant="error" description={error} inline={true} closable={true} onClose={() => setError(null)} />
          )}
          <p className="text-body-medium text-sys-brand-on-surface">
            Total Students to Mark as Eligible for Enrollment: <strong>{previewData.length}</strong>
          </p>
          <p className="text-body-medium text-sys-brand-on-surface">
            Are you sure you want to finalize this roster and mark these student(s) as eligible for enrollment? PGA
            National will be notified to review students and complete the enrollment process, at which time the students
            will be visible on the Current Students page.
          </p>
        </div>
      </Dialog>
    </FocusedPageLayout>
  );
}
